import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { FormikErrors } from 'formik';
import { useIntlByKey } from 'services/i18n/hooks/useIntlByKey';

import { FormRowContainer } from './SettingsForm.style';
import { SettingsFormKeys } from './settingsFormValues';
import { SettingsFormik } from './useSettingsFormik';

type TextFieldKey =
  | SettingsFormKeys.CONTACT_EMAIL
  | SettingsFormKeys.SUBSCRIPTION_REQUEST_EMAIL
  | SettingsFormKeys.INVITATION_REPLY_TO_EMAIL
  | SettingsFormKeys.SURCHARGE_PERCENTAGE
  | SettingsFormKeys.MAX_ALLOWED_DAYS_TO_PAY
  | SettingsFormKeys.ACH_LIMIT
  | SettingsFormKeys.CREDIT_CARD_LIMIT;

type SwitchFieldKey =
  | SettingsFormKeys.IS_PAYMENT_ENABLED
  | SettingsFormKeys.IS_PAYMENT_CUTOFF_ENABLED
  | SettingsFormKeys.SHOW_INVOICES
  | SettingsFormKeys.SHOW_DELIVERY_NOTES
  | SettingsFormKeys.SHOW_ACCOUNT_BALANCE
  | SettingsFormKeys.IS_INVOICE_NOTIFICATION_ENABLED
  | SettingsFormKeys.INVITATION_SHOW_USER_NAME;

export type SettingsSectionProps = {
  formik: SettingsFormik;
  isReadOnly: boolean;
};

export const getListError = (error: FormikErrors<unknown> | string | string[] | undefined) => {
  if (!error) {
    return undefined;
  }
  if (Array.isArray(error)) {
    return error.find(Boolean);
  }
  return typeof error === 'string' ? error : undefined;
};

export const useSettingsFieldHelpers = (formik: SettingsFormik, isReadOnly: boolean) => {
  const settingsFormIntl = useIntlByKey('settings-form');

  const getTextFieldProps = (key: TextFieldKey, labelKey: string, withPlaceholder = false) => ({
    id: key,
    name: key,
    label: settingsFormIntl(`form.${labelKey}.label`),
    placeholder: withPlaceholder ? settingsFormIntl(`form.${labelKey}.placeholder`) : undefined,
    value: formik.values[key],
    onChange: formik.handleChange,
    error: formik.touched[key] && Boolean(formik.errors[key]),
    helperText: formik.touched[key] && formik.errors[key],
    disabled: isReadOnly,
  });

  const renderSwitch = (key: SwitchFieldKey, labelKey: string) => (
    <FormRowContainer>
      <FormControlLabel
        control={
          <Switch
            id={key}
            name={key}
            color="primary"
            checked={formik.values[key]}
            onChange={formik.handleChange}
            disabled={isReadOnly}
          />
        }
        label={settingsFormIntl(`form.${labelKey}.label`)}
      />
    </FormRowContainer>
  );

  return { settingsFormIntl, getTextFieldProps, renderSwitch };
};
